/**
 * What Was Posted Modal
 * 
 * Displays detailed evidence for a specific store/campaign/operator posting.
 * Includes:
 * - Full evidence gallery
 * - Store signature
 * - Expected vs actual formats
 * - Posting event timeline
 * - Approval controls
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { http } from '@/lib/http';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  X, 
  Image as ImageIcon, 
  FileSignature, 
  CheckCircle2, 
  AlertCircle,
  Clock,
  MapPin,
  User,
  Package,
  Calendar,
  FileText,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Check,
  XCircle,
  Hash,
  Copy
} from 'lucide-react';

import type { WhatWasPostedRow } from '@/components/tables/what-was-posted-columns';
import { Smartphone } from 'lucide-react';

// Helper to format device info for display
function formatDeviceInfo(device: {
  platform: 'ios' | 'android' | 'web';
  deviceModel: string | null;
  osVersion: string | null;
  appVersion: string | null;
  appBuild: string | null;
} | null | undefined): string {
  if (!device) return '';
  
  const parts: string[] = [];
  
  if (device.deviceModel) {
    parts.push(device.deviceModel);
  }
  
  if (device.osVersion) {
    const osName = device.platform === 'ios' ? 'iOS' : 
                   device.platform === 'android' ? 'Android' : 'Web';
    parts.push(`${osName} ${device.osVersion}`);
  }
  
  if (device.appVersion) {
    const version = device.appBuild 
      ? `v${device.appVersion} (${device.appBuild})`
      : `v${device.appVersion}`;
    parts.push(`App ${version}`);
  }
  
  return parts.join(' • ') || 'Unknown device';
}

interface WhatWasPostedModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: WhatWasPostedRow | null;
  cycleId: number;
  onSuccess?: () => void;
}

interface ActualFormatDetail {
  formatId: number;
  formatName: string;
  expectedCount: number;
  actualCount: number;
  discrepancyReason: string | null;
  hasDiscrepancy: boolean;
  completionStatus: string;
  isExtra?: boolean;
}

interface TaskDetails {
  task: {
    id: number;
    // V2: Explicit assignmentId for admin correlation
    assignmentId?: number | null;
    campaignId: number;
    campaignName: string;
    cycleId: number;
    storeId: number;
    storeName: string;
    storeAddress: string;
    operatorId: number;
    operatorName: string;
    operatorUsername: string;
    packId: number;
    packName: string;
    status: string;
    adminApprovedAt: string | null;
    adminApprovedBy: number | null;
    adminNotes: string | null;
  };
  postingEvents: Array<{
    id: number;
    postedUnits: number;
    timestamp: string;
    notes: string | null;
    installerNote?: string | null;
    groupId: number | null;
    groupName: string | null;
    signedOffBy: number | null;
    signedOffAt: string | null;
    signOffType: string | null;
  }>;
  evidence: Array<{
    id: number;
    eventId: number;
    publicId: string;
    secureUrl: string;
    assetType: string;
    meta: any;
    createdAt: string;
    // Device metadata for evidence tracking (privacy-safe)
    device?: {
      platform: 'ios' | 'android' | 'web';
      deviceModel: string | null;
      osVersion: string | null;
      appVersion: string | null;
      appBuild: string | null;
    } | null;
  }>;
  expectedFormats: Array<{
    formatId: number;
    formatName: string;
    formatType: string;
    quantity: number;
  }>;
  // NEW: Actual format data with discrepancy tracking
  actualFormats?: ActualFormatDetail[];
  extraFormats?: ActualFormatDetail[];
  storeSignature: {
    id: number;
    signatureUrl: string | null;
    signoffMethod: 'signature' | 'typed_name' | null;
    managerName: string | null;
    timestamp: string;
    completionStatus: string;
    latitude: string | null;
    longitude: string | null;
  } | null;
}

export function WhatWasPostedModal({ 
  isOpen, 
  onClose, 
  row, 
  cycleId,
  onSuccess 
}: WhatWasPostedModalProps) {
  const queryClient = useQueryClient();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Fetch detailed task information
  const { data: details, isLoading } = useQuery<TaskDetails>({
    queryKey: ['/api/proof-of-posting/what-was-posted', row?.taskId, 'details'],
    queryFn: async () => {
      if (!row?.taskId) throw new Error('No task ID');
      const response = await http(`/api/proof-of-posting/what-was-posted/${row.taskId}/details`);
      if (!response.ok) throw new Error('Failed to fetch details');
      return response.json();
    },
    enabled: isOpen && !!row?.taskId,
  });

  /**
   * @deprecated DEPRECATED - Admin approval/rejection has moved to Tracker Review Tab
   * 
   * All approvals and rejections should now be done via:
   * /admin/activation-cockpit/review?retailerId={id}&cycleId={id}
   * 
   * This uses the /api/review/decisions endpoint which:
   * - Creates proper review_decisions records
   * - Supports REPOST task creation for rejections
   * - Sends push notifications to operators
   * - Maintains proper audit trail
   */
  const _showDeprecationToast = () => {
    toast({
      title: 'Action Moved',
      description: 'Please use the Tracker Review page (Cockpit → Review) to approve or reject submissions.',
      variant: 'destructive',
    });
  };

  const allImages = details?.evidence?.filter(e => 
    e.assetType === 'photo' || e.assetType === 'image'
  ) || [];
  const latestInstallerNote = details?.postingEvents?.find((event) => event.installerNote)?.installerNote || null;

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  const handlePrevImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex < allImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  if (!row) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span>Posting Evidence Details</span>
                {/* V2: Show Assignment ID for admin correlation */}
                {row.assignmentId != null && (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="font-mono text-xs">
                      <Hash className="h-3 w-3 mr-1" />
                      {row.assignmentId}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(String(row.assignmentId));
                        toast({
                          title: 'Copied',
                          description: `Assignment ID ${row.assignmentId} copied`,
                        });
                      }}
                      title="Copy Assignment ID"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
              <Badge variant={
                row.status === 'complete' ? 'default' :
                row.status === 'incomplete' ? 'secondary' : 'outline'
              }>
                {row.status === 'complete' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                {row.status === 'incomplete' && <AlertCircle className="h-3 w-3 mr-1" />}
                {row.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1">
            <div className="space-y-6 pr-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <MapPin className="h-3 w-3" />
                    Store
                  </div>
                  <div className="font-medium text-sm">{row.storeName}</div>
                </Card>
                <Card className="p-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <Package className="h-3 w-3" />
                    Campaign
                  </div>
                  <div className="font-medium text-sm">{row.campaignName}</div>
                </Card>
                <Card className="p-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <User className="h-3 w-3" />
                    Operator
                  </div>
                  <div className="font-medium text-sm">{row.operatorName}</div>
                </Card>
                <Card className="p-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <Calendar className="h-3 w-3" />
                    Posted
                  </div>
                  <div className="font-medium text-sm">
                    {row.postedAt ? format(new Date(row.postedAt), 'dd MMM yyyy') : 'N/A'}
                  </div>
                </Card>
              </div>

              {latestInstallerNote && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">Installer Note</p>
                    <p className="mt-1 text-sm text-amber-950">{latestInstallerNote}</p>
                  </CardContent>
                </Card>
              )}

              <Tabs defaultValue="evidence" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="evidence">
                    <ImageIcon className="h-4 w-4 mr-1" />
                    Evidence ({allImages.length})
                  </TabsTrigger>
                  <TabsTrigger value="formats">
                    <Package className="h-4 w-4 mr-1" />
                    Formats
                  </TabsTrigger>
                  <TabsTrigger value="signature">
                    <FileSignature className="h-4 w-4 mr-1" />
                    Signature
                  </TabsTrigger>
                  <TabsTrigger value="timeline">
                    <Clock className="h-4 w-4 mr-1" />
                    Timeline
                  </TabsTrigger>
                </TabsList>

                {/* Evidence Tab */}
                <TabsContent value="evidence" className="mt-4">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading evidence...
                    </div>
                  ) : allImages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No evidence images uploaded</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                      {allImages.map((img, idx) => {
                        const deviceInfo = formatDeviceInfo((img as any).device);
                        return (
                          <div key={img.id} className="space-y-1">
                            <button
                              onClick={() => handleImageClick(idx)}
                              className="relative aspect-square rounded-lg border overflow-hidden hover:border-primary transition-colors group w-full"
                            >
                              <img
                                src={img.secureUrl}
                                alt={`Evidence ${idx + 1}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="absolute bottom-1 left-1 right-1">
                                <Badge variant="secondary" className="text-xs">
                                  {format(new Date(img.createdAt), 'HH:mm')}
                                </Badge>
                              </div>
                            </button>
                            {/* Device info below image */}
                            {deviceInfo && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground px-1">
                                <Smartphone className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate" title={deviceInfo}>{deviceInfo}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                {/* Formats Tab */}
                <TabsContent value="formats" className="mt-4">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading formats...
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Format Table: EXPECTED vs DELIVERED */}
                      <div className="text-sm font-medium mb-2">
                        Format Status for {row.packName}
                      </div>
                      
                      {details?.actualFormats && details.actualFormats.length > 0 ? (
                        <>
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/50">
                                  <TableHead className="font-semibold">Format</TableHead>
                                  <TableHead className="text-center font-semibold w-24">EXPECTED</TableHead>
                                  <TableHead className="text-center font-semibold w-24">DELIVERED</TableHead>
                                  <TableHead className="text-center font-semibold w-24">Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {details.actualFormats.map(fmt => {
                                  const hasDiscrepancy = fmt.actualCount < fmt.expectedCount;
                                  return (
                                    <TableRow 
                                      key={fmt.formatId}
                                      className={hasDiscrepancy ? 'bg-amber-50/50' : ''}
                                    >
                                      <TableCell className="font-medium">{fmt.formatName}</TableCell>
                                      <TableCell className="text-center">
                                        <span className="text-muted-foreground">{fmt.expectedCount}</span>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <span className={hasDiscrepancy ? 'text-amber-700 font-medium' : 'text-green-700 font-medium'}>
                                          {fmt.actualCount}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        {fmt.actualCount >= fmt.expectedCount ? (
                                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                            <CheckCircle2 className="h-3 w-3 mr-1" />
                                            Complete
                                          </Badge>
                                        ) : fmt.actualCount > 0 ? (
                                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                            <AlertCircle className="h-3 w-3 mr-1" />
                                            Partial
                                          </Badge>
                                        ) : (
                                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                            <XCircle className="h-3 w-3 mr-1" />
                                            Missing
                                          </Badge>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                          
                          {/* Extra Formats (not in original assignment) */}
                          {details.extraFormats && details.extraFormats.length > 0 && (
                            <div className="mt-4">
                              <div className="text-sm text-muted-foreground mb-2">
                                Additional formats delivered (not in original assignment):
                              </div>
                              <div className="border rounded-lg overflow-hidden">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-blue-50/50">
                                      <TableHead className="font-semibold">Format</TableHead>
                                      <TableHead className="text-center font-semibold w-24">EXPECTED</TableHead>
                                      <TableHead className="text-center font-semibold w-24">DELIVERED</TableHead>
                                      <TableHead className="text-center font-semibold w-24">Status</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {details.extraFormats.map(fmt => (
                                      <TableRow key={`extra-${fmt.formatId}`} className="bg-blue-50/30">
                                        <TableCell className="font-medium">
                                          {fmt.formatName}
                                          <Badge variant="outline" className="ml-2 text-xs">Extra</Badge>
                                        </TableCell>
                                        <TableCell className="text-center text-muted-foreground">—</TableCell>
                                        <TableCell className="text-center">
                                          <span className="text-blue-700 font-medium">+{fmt.actualCount}</span>
                                        </TableCell>
                                        <TableCell className="text-center">
                                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                            Added
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          )}
                        </>
                      ) : details?.expectedFormats && details.expectedFormats.length > 0 ? (
                        // Fallback: Show expected formats when no actual delivery data
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/50">
                                <TableHead className="font-semibold">Format</TableHead>
                                <TableHead className="text-center font-semibold w-24">EXPECTED</TableHead>
                                <TableHead className="text-center font-semibold w-24">DELIVERED</TableHead>
                                <TableHead className="text-center font-semibold w-24">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {details.expectedFormats.map(fmt => (
                                <TableRow key={fmt.formatId}>
                                  <TableCell className="font-medium">{fmt.formatName}</TableCell>
                                  <TableCell className="text-center text-muted-foreground">{fmt.quantity}</TableCell>
                                  <TableCell className="text-center text-muted-foreground">—</TableCell>
                                  <TableCell className="text-center">
                                    <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                                      <Clock className="h-3 w-3 mr-1" />
                                      Pending
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground border rounded-lg">
                          No format requirements found
                        </div>
                      )}
                      
                      {/* Totals Section */}
                      <Separator className="my-4" />
                      <div className="flex justify-between text-sm">
                        <span>Total Evidence Photos:</span>
                        <span className="font-medium">{allImages.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Format Completion:</span>
                        <span className="font-medium">
                          {row.completedFormatCount}/{row.expectedFormatCount}
                        </span>
                      </div>
                      
                      {/* REASON FOR DISCREPANCY Section - only shown if discrepancy exists */}
                      {(() => {
                        // Collect discrepancy notes from formats where DELIVERED < EXPECTED
                        const discrepancyNotes: string[] = [];
                        
                        details?.actualFormats?.forEach(fmt => {
                          if (fmt.actualCount < fmt.expectedCount && fmt.discrepancyReason && fmt.discrepancyReason.trim()) {
                            if (!discrepancyNotes.includes(fmt.discrepancyReason.trim())) {
                              discrepancyNotes.push(fmt.discrepancyReason.trim());
                            }
                          }
                        });
                        
                        // Also check posting events for notes
                        details?.postingEvents?.forEach(event => {
                          if (event.notes && event.notes.trim()) {
                            if (!discrepancyNotes.includes(event.notes.trim())) {
                              discrepancyNotes.push(event.notes.trim());
                            }
                          }
                        });
                        
                        // Check row-level notes as fallback
                        if (discrepancyNotes.length === 0 && row.notes && row.notes.trim()) {
                          discrepancyNotes.push(row.notes.trim());
                        }
                        
                        if (discrepancyNotes.length === 0) return null;
                        
                        return (
                          <div className="mt-4 p-4 border border-amber-200 rounded-lg bg-amber-50/30">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="h-4 w-4 text-amber-600" />
                              <span className="font-semibold text-amber-800 uppercase text-sm">
                                Reason for Discrepancy (from Note)
                              </span>
                            </div>
                            <div className="space-y-2">
                              {discrepancyNotes.map((note, idx) => (
                                <p key={idx} className="text-sm text-gray-800 whitespace-pre-wrap bg-white p-3 rounded border border-amber-100">
                                  {note}
                                </p>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </TabsContent>

                {/* Signature Tab */}
                <TabsContent value="signature" className="mt-4">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading signature...
                    </div>
                  ) : details?.storeSignature ? (
                    <div className="space-y-4">
                      <Card className="p-4">
                        {details.storeSignature.signatureUrl ? (
                          <div className="aspect-video relative rounded-lg border overflow-hidden bg-gray-50">
                            <img
                              src={details.storeSignature.signatureUrl}
                              alt="Store Signature"
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                console.error('[SIGNATURE_ERROR] Failed to load signature image:', {
                                  url: details.storeSignature?.signatureUrl,
                                  timestamp: details.storeSignature?.timestamp
                                });
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent && !parent.querySelector('.signature-error-fallback')) {
                                  const fallback = document.createElement('div');
                                  fallback.className = 'signature-error-fallback flex flex-col items-center justify-center h-full text-muted-foreground';
                                  fallback.innerHTML = '<p class="text-sm">Signature image unavailable</p><p class="text-xs mt-1">URL may have expired</p>';
                                  parent.appendChild(fallback);
                                }
                              }}
                              onLoad={() => {
                                console.log('[SIGNATURE_SUCCESS] Signature image loaded successfully');
                              }}
                            />
                          </div>
                        ) : (
                          <div className="rounded-lg border bg-gray-50 p-6 text-center">
                            <p className="text-sm text-muted-foreground">Typed manager sign-off</p>
                            <p className="mt-2 text-lg font-semibold text-gray-900">
                              {details.storeSignature.managerName || 'Manager name unavailable'}
                            </p>
                          </div>
                        )}
                        <div className="mt-3 space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Signed At:</span>
                            <span>{format(new Date(details.storeSignature.timestamp), 'dd MMM yyyy HH:mm')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Method:</span>
                            <span>{details.storeSignature.signoffMethod === 'typed_name' ? 'Entered name' : 'Signature'}</span>
                          </div>
                          {details.storeSignature.managerName && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Manager:</span>
                              <span>{details.storeSignature.managerName}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Status:</span>
                            <Badge variant="outline">{details.storeSignature.completionStatus}</Badge>
                          </div>
                          {details.storeSignature.latitude && details.storeSignature.longitude && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Location:</span>
                              <span className="text-xs">
                                {parseFloat(details.storeSignature.latitude).toFixed(4)}, 
                                {parseFloat(details.storeSignature.longitude).toFixed(4)}
                              </span>
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileSignature className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No store signature captured</p>
                    </div>
                  )}
                </TabsContent>

                {/* Timeline Tab */}
                <TabsContent value="timeline" className="mt-4">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading timeline...
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {details?.postingEvents?.map((event, idx) => (
                        <div 
                          key={event.id}
                          className="flex gap-4 p-3 border rounded-lg"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-medium">{idx + 1}</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-sm">
                                Posted {event.postedUnits} unit(s)
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(event.timestamp), 'dd MMM yyyy HH:mm')}
                              </div>
                            </div>
                            {event.notes && (
                              <div className="text-sm text-muted-foreground mt-1">
                                <FileText className="h-3 w-3 inline mr-1" />
                                {event.notes}
                              </div>
                            )}
                            {event.groupName && (
                              <Badge variant="outline" className="mt-2 text-xs">
                                Group: {event.groupName}
                              </Badge>
                            )}
                            {event.signedOffAt && (
                              <div className="mt-2 text-xs text-green-600">
                                <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                Signed off {formatDistanceToNow(new Date(event.signedOffAt), { addSuffix: true })}
                                {event.signOffType && ` (${event.signOffType})`}
                              </div>
                            )}
                          </div>
                        </div>
                      )) || (
                        <div className="text-center py-4 text-muted-foreground">
                          No posting events recorded
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-4">
                        Event IDs: {row.postingEventIds?.join(', ') || 'N/A'}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* DEPRECATED: Approval Actions - use Tracker Review Tab instead */}
              {row.status === 'incomplete' && (
                <Card className="p-4 bg-amber-50 border-amber-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-amber-800">Review via Tracker</div>
                      <div className="text-sm text-amber-700">
                        Approvals and rejections are now handled in <strong>Cockpit → Review</strong>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Lightbox for full-size image viewing */}
      {lightboxOpen && selectedImageIndex !== null && allImages[selectedImageIndex] && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="h-6 w-6" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevImage();
            }}
            disabled={selectedImageIndex === 0}
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>

          <img
            src={allImages[selectedImageIndex].secureUrl}
            alt={`Evidence ${selectedImageIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              handleNextImage();
            }}
            disabled={selectedImageIndex === allImages.length - 1}
          >
            <ChevronRight className="h-8 w-8" />
          </Button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {selectedImageIndex + 1} / {allImages.length}
          </div>
        </div>
      )}
    </>
  );
}

export default WhatWasPostedModal;
